import { type Ref, ref } from 'vue'
import { api } from '@/api'

/** How much of the machine's vocabulary a screen shows: Standard hides IDs, tool names and error codes. */
export type UiMode = 'standard' | 'advanced'

/**
 * Where a browser kept the choice before it moved to the account (#1949). Read only to carry that choice over to the
 * account once, then removed; nothing is ever written to it again.
 */
const STORAGE_KEY = 'zh.uiMode'

/**
 * The browser's saved settings, or null where there are none to use: a private window can refuse access, and the
 * test runner has no working `localStorage` unless a spec installs one. There is then simply nothing to carry over.
 */
function savedSettings(): Storage | null {
    try {
        const storage = globalThis.localStorage
        return storage && typeof storage.getItem === 'function' ? storage : null
    } catch {
        return null
    }
}

/** The choice this browser made before #1949 and has not yet carried over to the account, or null for none. */
function browserChoice(): UiMode | null {
    try {
        const saved = savedSettings()?.getItem(STORAGE_KEY)
        return saved === 'standard' || saved === 'advanced' ? saved : null
    } catch {
        return null
    }
}

/** Removes the browser's old choice once the account holds one: from then on only the account decides. */
function forgetBrowserChoice(): void {
    try {
        savedSettings()?.removeItem(STORAGE_KEY)
    } catch {
        // Nothing can be removed where storage refuses access, and nothing would be read from it either.
    }
}

/**
 * One mode for the whole portal: every component that asks gets this same ref. Standard until the account answers,
 * whatever the browser holds: it is the safe default, because it hides more (#1949, CTO decision 7).
 */
const mode = ref<UiMode>('standard')

/** The value the account last confirmed, which a failed save goes back to. Never chose reads as Standard. */
let lastSaved: UiMode = 'standard'

/**
 * Whether this page has seen the account hold a value, from `GET /auth/me` or a write that succeeded. A person's
 * choice can never go back to none, so a later "no choice yet" is a read that raced that write and changes nothing.
 */
let accountHasChoice = false

/** The carry-over write in flight, so a second load while it runs waits for it rather than copying twice. */
let carrying: Promise<void> | null = null

/**
 * The name every open portal page of this browser listens on for a mode the account has just saved (#2101). Only
 * pages of the same origin hear it, and nothing is kept: a page that opens later reads the account instead.
 */
const MODE_CHANNEL = 'zh.uiMode.saved'

/**
 * This page's line to the other open pages of the same browser, or null where the runtime has none to offer (an old
 * browser, or one that refuses it). The mode then still follows the account on the next load, as before #2101.
 */
const channel = openModeChannel()

function openModeChannel(): BroadcastChannel | null {
    try {
        if (typeof BroadcastChannel !== 'function') {
            return null
        }
        const opened = new BroadcastChannel(MODE_CHANNEL)
        opened.onmessage = (event: MessageEvent<unknown>) => heardFromAnotherPage(event.data)
        return opened
    } catch {
        return null
    }
}

/**
 * Another open page saved `data` to the account (#2101, CTO decision 4): this page takes it as what the account holds,
 * so every Details toggle and every other screen here follows with no reload, and a failed save later goes back to it.
 * It writes nothing, to the account or to the browser: the page that saved has already done both.
 */
function heardFromAnotherPage(data: unknown): void {
    const saved = (data as { mode?: unknown } | null)?.mode
    if (saved !== 'standard' && saved !== 'advanced') {
        return
    }
    accountHasChoice = true
    lastSaved = saved
    mode.value = saved
}

/** Tells the other open pages of this browser what the account now holds. Only ever after a write that succeeded. */
function tellOtherPages(saved: UiMode): void {
    try {
        channel?.postMessage({ mode: saved })
    } catch {
        // A page that cannot be told still reads the account when it next loads.
    }
}

/**
 * Sets the shared mode for this page only, with no network: the account is changed through {@link saveUiMode}. Every
 * #1749 screen keeps this setter's shape, and specs use it to put a screen in either mode.
 */
function setMode(next: UiMode): void {
    mode.value = next
}

/** The portal-wide Standard/Advanced mode (#1749): Standard by default, saved on the person's account (#1949). */
export function useUiMode(): { mode: Ref<UiMode>; setMode: (mode: UiMode) => void } {
    return { mode, setMode }
}

/** Takes `value` as what the account holds. */
function accountHolds(value: UiMode): void {
    accountHasChoice = true
    lastSaved = value
    mode.value = value
    forgetBrowserChoice()
}

/**
 * Copies the browser's old choice to an account that has none (#1949, CTO decisions 4 and 6). The choice applies to
 * this page either way. When the copy fails the key stays, so the next load tries again. A copy that lands is a save
 * like any other, so the other open pages are told (#2101).
 */
async function carryOver(carried: UiMode): Promise<void> {
    mode.value = carried
    try {
        await api.saveUiMode(carried)
    } catch {
        return
    }
    accountHolds(carried)
    tellOtherPages(carried)
}

/**
 * Applies what `GET /auth/me` says this person chose (#1949): the account's value when it has one, otherwise a
 * browser value carried over to the account once. Resolves when any carry-over write has settled; never rejects.
 * `undefined` is an API that does not report the mode at all, which changes nothing and carries nothing over.
 */
export async function loadUiMode(account: UiMode | null | undefined): Promise<void> {
    if (account === undefined) {
        return
    }
    if (account !== null) {
        accountHolds(account)
        return
    }
    if (accountHasChoice) {
        return
    }
    if (!carrying) {
        const carried = browserChoice()
        if (carried === null) {
            mode.value = 'standard'
            return
        }
        carrying = carryOver(carried).finally(() => {
            carrying = null
        })
    }
    await carrying
}

/**
 * Changes the shared mode and saves it to the account (#1949) with one write. Rejects, and returns the mode to the
 * last saved value, when the save fails. A browser choice still being carried over is older than this one, so it is
 * let land first: the person's write always reaches the account after it, and the switch keeps showing their pick.
 * Once the save succeeds, the other open pages of this browser are told and follow with no reload (#2101); a refused
 * save tells them nothing.
 */
export async function saveUiMode(next: UiMode): Promise<void> {
    mode.value = next
    if (carrying !== null) {
        await carrying
        mode.value = next
    }
    try {
        await api.saveUiMode(next)
    } catch (error) {
        mode.value = lastSaved
        throw error
    }
    accountHolds(next)
    tellOtherPages(next)
}
